import { NotifyHelper } from 'classes/helpers/notify.helper';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { CrudAction } from 'enums/tables';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { S3Command } from 'lib_ts/enums/data.enums';
import {
  HITTER_SIDES,
  HitterDataSource,
  HitterRosterStatus,
} from 'lib_ts/enums/hitters.enums';
import { AwsRegion } from 'lib_ts/enums/s3.enum';
import { WithID } from 'lib_ts/interfaces/common/i-generics';
import {
  IHitter,
  IHitterStats,
  IHitterUpsert,
} from 'lib_ts/interfaces/i-hitter';
import { clone, compact } from 'lodash';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AdminTeamsService } from 'services/admin/teams.service';
import { HittersService } from 'services/hitters.service';
import { useAuthStore } from 'stores/use-auth-store';
import useSignedUrlsStore from 'stores/use-signed-urls-store';

const CONTEXT_NAME = 'HittersContext';

/** values used for spawning and resetting stats entries */
export const getEmptyStats = (): IHitterStats => ({
  pitches: 0,
  swings: 0,
  hits: 0,
});

interface ICrudConfig {
  key: number;
  action: CrudAction;
  models: IHitter[];
  onClose?: () => void;
}

interface IFilterState extends Partial<IHitter> {}

interface IHitterStatsDict {
  [hitterID: string]: IHitterStats;
}

export interface IHittersContext {
  // i.e. whether the user's team has an org ID for Stats API
  statsApiImport: boolean;

  lastChanged: number;

  filters: IFilterState;
  readonly setFilters: (filters: IFilterState) => void;

  filtered: IHitter[];

  active: IHitter | undefined;

  hitters: IHitter[];

  hitterOptions: IOptionExt[];

  stats: IHitterStatsDict;

  loading: boolean;

  dialogCRUD?: ICrudConfig;

  /** change the active_id */
  readonly setActive: (hitter_id?: string) => void;

  readonly increasePitchCount: (hitter_id: string) => Promise<IHitterStats>;
  /** reverts the stats entry for a given hitter to the default (starting) values */
  readonly resetStats: (hitter_id: string) => void;
  readonly create: (
    payload: IHitterUpsert,
    onCreate?: (value: IHitter) => void,
    customAvatarFile?: File
  ) => Promise<boolean>;
  readonly update: (config: {
    payload: WithID<IHitterUpsert>;
    onUpdate?: (value: IHitter) => void;
    customAvatarFile?: File;
    silently?: boolean;
  }) => Promise<boolean>;
  readonly confirmDeleteMany: (values: IHitter[]) => void;

  readonly openCrudDialog: (config: ICrudConfig) => void;
  readonly closeCrudDialog: () => void;

  readonly uploadCsv: (file: File) => Promise<IHitter[] | undefined>;

  readonly setLastChanged: (timestamp: number) => void;

  readonly deleteHitters: (ids: string[]) => Promise<boolean>;
}

const DEFAULT: IHittersContext = {
  statsApiImport: false,

  lastChanged: Date.now(),

  active: undefined,

  filters: {},
  setFilters: () => console.error(`${CONTEXT_NAME}: not init`),
  filtered: [],

  hitters: [],
  hitterOptions: [],

  stats: {},

  loading: false,

  setActive: () => console.error(`${CONTEXT_NAME}: not init`),
  increasePitchCount: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  resetStats: () => console.error(`${CONTEXT_NAME}: not init`),
  create: () => new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  confirmDeleteMany: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  update: () => new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),

  openCrudDialog: () => console.error(`${CONTEXT_NAME}: not init`),
  closeCrudDialog: () => console.error(`${CONTEXT_NAME}: not init`),

  uploadCsv: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),

  setLastChanged: () => console.error(`${CONTEXT_NAME}: not init`),

  deleteHitters: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
};

export const HittersContext = createContext(DEFAULT);

interface IProps {
  children: ReactNode;
}

const uploadHitterAvatar = async (
  hitterID: string,
  file: File
): Promise<IHitter | undefined> => {
  const formData = new FormData();
  formData.append('files', file);

  const updatedHitter = await HittersService.getInstance().uploadHitterAvatar(
    hitterID,
    formData
  );

  return updatedHitter;
};

export const HittersProvider: FC<IProps> = (props) => {
  const [active, setActive] = useState(DEFAULT.active);

  const [hitters, setHitters] = useState(DEFAULT.hitters);

  const [filters, setFilters] = useState(DEFAULT.filters);
  const [hitterStats, setHitterStats] = useState(DEFAULT.stats);
  const [loading, setLoading] = useState(DEFAULT.loading);

  const latestHitterStats = useLatest(hitterStats);

  const current = useAuthStore((s) => s.current);

  const [dialogCRUD, setDialogCRUD] = useState<ICrudConfig>();

  const [lastChanged, setLastChanged] = useState(Date.now());

  /** reload data to match session access */
  useEffect(() => {
    if (!current.auth) {
      return;
    }

    if (!current.session) {
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setLoading(true);

        const results = await HittersService.getInstance().getHitters();
        const avatarPaths = ArrayHelper.unique(
          results.map((h) => h.avatar_path ?? '')
        );

        if (avatarPaths.length > 0) {
          await useSignedUrlsStore.getState().fetchSignedUrls(
            compact(
              results.map((m) =>
                m.avatar_path
                  ? {
                      path: m.avatar_path,
                      region: m.avatar_region ?? AwsRegion.CA,
                    }
                  : undefined
              )
            ),
            S3Command.getObject
          );
        }

        setHitters(results);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [current.auth, current.session, lastChanged]);

  const filtered = useMemo(() => {
    const safeNameFilter = filters.name?.toLowerCase() ?? '';

    return hitters
      .filter((h) => {
        if (!safeNameFilter) {
          return true;
        }

        return h.name.toLowerCase().includes(safeNameFilter);
      })
      .filter((h) => !filters.side || filters.side === h.side)
      .filter((h) => !filters.level || filters.level === h.level)
      .filter((h) => {
        if (!filters.status) {
          return true;
        }

        switch (filters.status) {
          case HitterRosterStatus.NA: {
            // no status => old hitter, treat the same as manual
            return !h.status || h.status === HitterRosterStatus.NA;
          }

          case HitterRosterStatus.OffRoster:
          case HitterRosterStatus.OnRoster:
          default: {
            return filters.status === h.status;
          }
        }
      })
      .filter((h) => {
        if (!filters.source) {
          return true;
        }

        switch (filters.source) {
          case HitterDataSource.Manual: {
            // no source => old hitter, treat the same as manual
            return !h.source || h.source === HitterDataSource.Manual;
          }

          case HitterDataSource.StatsApi:
          case HitterDataSource.Csv:
          default: {
            return filters.source === h.source;
          }
        }
      })
      .sort((a, b) => StringHelper.safeSort(a.name, b.name));
  }, [hitters, filters]);

  const [statsApiImport, setStatsApiImport] = useState(DEFAULT.statsApiImport);

  useEffect(() => {
    AdminTeamsService.getInstance()
      .getTeams(false)
      .then((teams) => {
        const team = teams.find((t) => t._id === current.teamID);
        setStatsApiImport(
          !!team?.stats_api_orgId && !!team.enable_hitters_import
        );
      });
  }, [current.teamID]);

  const hitterOptions = useMemo(() => {
    const sidesDict = ArrayHelper.firstBy(HITTER_SIDES, 'value');

    return hitters
      .map((h) => {
        const source = h.source || HitterDataSource.Manual;
        const o: IOptionExt = {
          label: h.name,
          value: h._id,
          group: source,
          suffixLabel: t(sidesDict[h.side]?.label ?? '').toString(),
          suffix: <HitterSideBadge value={h.side} />,
        };

        return o;
      })
      .sort((a, b) => StringHelper.safeSort(a.label, b.label));
  }, [hitters]);

  const replaceHitter = useCallback((newHitter: IHitter) => {
    setHitters((prev) => {
      const index = prev.findIndex((v) => v._id === newHitter._id);

      if (index === -1) {
        return [...prev, newHitter];
      }

      const next = [...prev];
      next.splice(index, 1, newHitter);
      return next;
    });
  }, []);

  const state: IHittersContext = {
    statsApiImport: statsApiImport,

    lastChanged: lastChanged,

    filters: filters,
    setFilters: setFilters,
    filtered: filtered,

    hitters: hitters,
    hitterOptions: hitterOptions,

    stats: hitterStats,
    loading: loading,

    active: active,

    dialogCRUD: dialogCRUD,

    setActive: async (hitter_id) => {
      if (!hitter_id) {
        setActive(undefined);
        return;
      }

      setActive(hitters.find((h) => h._id === hitter_id));

      const updatedHitter = await HittersService.getInstance().updateHitter({
        _id: hitter_id,
        lastActive: new Date().toISOString(),
      });

      replaceHitter(updatedHitter);

      // just so our active hitter has the updated lastActive value
      setActive(updatedHitter);
    },

    increasePitchCount: async (hitter_id) => {
      const nextDict = clone(latestHitterStats.current);

      if (!nextDict[hitter_id]) {
        nextDict[hitter_id] = getEmptyStats();
      }

      nextDict[hitter_id].pitches++;
      setHitterStats(nextDict);

      return nextDict[hitter_id];
    },

    resetStats: (hitter_id) => {
      setHitterStats((prev) => {
        const nextDict = clone(prev);
        nextDict[hitter_id] = getEmptyStats();
        return nextDict;
      });
    },

    confirmDeleteMany: (values) =>
      setDialogCRUD({
        key: Date.now(),
        action: CrudAction.Delete,
        models: values,
      }),

    create: async (payload, onCreate, customAvatarFile?: File) => {
      try {
        setLoading(true);

        let newHitter =
          await HittersService.getInstance().createHitter(payload);

        // in case we're creating a new hitter, we'll need to upload the avatar after we have the hitter ID
        if (customAvatarFile) {
          newHitter =
            (await uploadHitterAvatar(newHitter._id, customAvatarFile)) ||
            newHitter; // make TS stop complaining
        }

        replaceHitter(newHitter);

        NotifyHelper.success({ message_md: 'Hitter created!' });

        if (onCreate) {
          onCreate(newHitter);
        }

        return true;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });

        return false;
      } finally {
        setLoading(false);
      }
    },

    update: async (config) => {
      try {
        const { payload, customAvatarFile, silently, onUpdate } = config;

        setLoading(true);

        let updatedHitter =
          await HittersService.getInstance().updateHitter(payload);

        // need to upload file to S3 and update hitter avatar_path
        if (customAvatarFile) {
          updatedHitter =
            (await uploadHitterAvatar(updatedHitter._id, customAvatarFile)) ||
            updatedHitter; // make TS stop complaining
        }

        replaceHitter(updatedHitter);

        if (active?._id === updatedHitter._id) {
          setActive(updatedHitter);
        }

        if (!silently) {
          NotifyHelper.success({
            message_md: `${updatedHitter.name} was updated successfully.`,
          });
        }

        if (onUpdate) {
          onUpdate(updatedHitter);
        }

        return true;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });

        return false;
      } finally {
        setLoading(false);
      }
    },

    openCrudDialog: (config) => {
      setDialogCRUD(config);
    },

    closeCrudDialog: () => {
      setDialogCRUD(undefined);
    },

    uploadCsv: async (file) => {
      setLoading(true);

      /** append the file */
      const formData = new FormData();
      formData.append('files', file);

      const upsertedHitters = await HittersService.getInstance()
        .uploadCsv(formData)
        .finally(() => setLoading(false));

      if (!upsertedHitters || upsertedHitters.length === 0) {
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return undefined;
      }

      setHitters((prev) => {
        const nextHitters = [...prev];

        for (const upserted of upsertedHitters) {
          const index = nextHitters.findIndex((h) => h._id === upserted._id);

          if (index === -1) {
            nextHitters.push(upserted);
            continue;
          }

          nextHitters[index] = upserted;
        }

        return nextHitters;
      });

      return upsertedHitters;
    },

    deleteHitters: async (ids: string[]) => {
      try {
        setLoading(true);

        const success = await HittersService.getInstance().deleteHitters(ids);

        if (!success) {
          throw new Error('failed to delete hitters');
        }

        setDialogCRUD(undefined);

        NotifyHelper.success({
          message_md:
            ids.length === 1
              ? 'Hitter deleted.'
              : `${ids.length} hitters deleted.`,
        });

        setTimeout(() => {
          /** remove from context */
          setHitters((prev) => {
            const newValues = prev.filter((v) => !ids.includes(v._id));
            return newValues;
          });
        }, 500);

        return success;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });

        return false;
      } finally {
        setLoading(false);
      }
    },

    setLastChanged: setLastChanged,
  };

  useEffect(() => {
    // active selection may not be visible after filtering
    setActive(undefined);
  }, [filters]);

  return (
    <HittersContext.Provider value={state}>
      {props.children}
    </HittersContext.Provider>
  );
};
